/**
 * Performance Chart Component
 * Display monthly performance statistics with charts
 */

import { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { affiliateApi } from '../../api/affiliate';
import LoadingSpinner from '../ui/LoadingSpinner';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function PerformanceChart() {
  const [monthlyStats, setMonthlyStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [months, setMonths] = useState(6); // Default to 6 months

  useEffect(() => {
    loadMonthlyStats();
  }, [months]);

  const loadMonthlyStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await affiliateApi.getMonthlyStats(months);
      setMonthlyStats(response.monthlyStats);
    } catch (err: any) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-800/80 rounded-lg border border-slate-700/50 p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-800/80 rounded-lg border border-slate-700/50 p-6">
        <div className="text-center text-red-400">{error}</div>
      </div>
    );
  }

  const labels = monthlyStats.map((stat) => {
    const [year, month] = stat.month.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Clicks',
        data: monthlyStats.map((stat) => stat.clicks),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Referrals',
        data: monthlyStats.map((stat) => stat.referrals),
        borderColor: 'rgb(168, 85, 247)',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Conversions',
        data: monthlyStats.map((stat) => stat.conversions),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const earningsChartData = {
    labels,
    datasets: [
      {
        label: 'Earnings ($)',
        data: monthlyStats.map((stat) => stat.earnings),
        borderColor: 'rgb(251, 191, 36)',
        backgroundColor: 'rgba(251, 191, 36, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#9CA3AF',
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#4B5563',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(75, 85, 99, 0.2)',
        },
        ticks: {
          color: '#9CA3AF',
        },
      },
      y: {
        grid: {
          color: 'rgba(75, 85, 99, 0.2)',
        },
        ticks: {
          color: '#9CA3AF',
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="bg-slate-800/80 rounded-lg border border-slate-700/50 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Performance Overview</h2>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-slate-400">Time Period:</label>
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value))}
            className="bg-slate-700/50 text-white rounded-lg px-3 py-1 text-sm border border-gray-600 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          >
            <option value={3}>Last 3 Months</option>
            <option value={6}>Last 6 Months</option>
            <option value={12}>Last 12 Months</option>
          </select>
        </div>
      </div>

      {/* Activity Chart */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-400 mb-4">Activity Metrics</h3>
        <div style={{ height: '300px' }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Earnings Chart */}
      <div>
        <h3 className="text-sm font-medium text-slate-400 mb-4">Earnings</h3>
        <div style={{ height: '200px' }}>
          <Line data={earningsChartData} options={chartOptions} />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-slate-700/50">
        <div className="text-center">
          <p className="text-2xl font-bold text-orange-400">
            {monthlyStats.reduce((sum, stat) => sum + stat.clicks, 0).toLocaleString()}
          </p>
          <p className="text-sm text-slate-400">Total Clicks</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-400">
            {monthlyStats.reduce((sum, stat) => sum + stat.referrals, 0).toLocaleString()}
          </p>
          <p className="text-sm text-slate-400">Total Referrals</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-400">
            {monthlyStats.reduce((sum, stat) => sum + stat.conversions, 0).toLocaleString()}
          </p>
          <p className="text-sm text-slate-400">Total Conversions</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-yellow-400">
            ${monthlyStats
              .reduce((sum, stat) => sum + stat.earnings, 0)
              .toFixed(2)}
          </p>
          <p className="text-sm text-slate-400">Total Earnings</p>
        </div>
      </div>
    </div>
  );
}
